import { formatLengthMm, formatSheetSize, formatAreaMm2, formatLengthInputMm, parseLengthToken } from "@plaatplan/core";
import { localeTag, normalizeLocale, t } from "./translate.js";

/** @typedef {"metric" | "imperial"} UnitSystem */

export const UNIT_SYSTEMS = ["metric", "imperial"];
export const DEFAULT_UNIT_SYSTEM = "metric";

/**
 * @param {string | undefined} unitSystem
 * @returns {UnitSystem}
 */
export function normalizeUnitSystem(unitSystem) {
  return unitSystem === "imperial" ? "imperial" : DEFAULT_UNIT_SYSTEM;
}

/**
 * @param {string | undefined} languageCode
 * @returns {UnitSystem}
 */
export function unitSystemForLanguage(languageCode) {
  const base = String(languageCode || "").split("-")[0].toLowerCase();
  return base === "en" ? "imperial" : DEFAULT_UNIT_SYSTEM;
}

/**
 * @param {{ locale?: string, unitSystem?: UnitSystem }} [options]
 */
export function createDisplay(options = {}) {
  const locale = normalizeLocale(options.locale);
  const unitSystem = normalizeUnitSystem(
    options.unitSystem ?? unitSystemForLanguage(locale),
  );

  /**
   * @param {string} key
   * @param {Record<string, string | number | undefined | null>} [params]
   */
  const translate = (key, params) => t(key, locale, params);

  /** @param {number} mm */
  const formatLength = (mm) => formatLengthMm(mm, { system: unitSystem });

  /** @param {number} lengthMm @param {number} widthMm */
  const formatSheet = (lengthMm, widthMm) => formatSheetSize(lengthMm, widthMm, {
    system: unitSystem,
  });

  /** @param {number} thicknessMm */
  const formatThickness = (thicknessMm) => formatLength(thicknessMm);

  /**
   * @param {number} lengthMm
   * @param {number} widthMm
   */
  const formatPanelSize = (lengthMm, widthMm) => `${formatLength(lengthMm)} × ${formatLength(widthMm)}`;

  /** @param {string} text */
  const parseLength = (text) => parseLengthToken(text, { defaultSystem: unitSystem });

  /** @param {number} mm */
  const formatLengthInput = (mm) => formatLengthInputMm(mm, unitSystem);

  /** @param {number} mm2 */
  const formatArea = (mm2) => formatAreaMm2(mm2, { system: unitSystem });

  return {
    locale,
    unitSystem,
    localeTag: localeTag(locale),
    t: translate,
    formatLength,
    formatSheet,
    formatThickness,
    formatPanelSize,
    parseLength,
    formatLengthInput,
    formatArea,
  };
}
